import { ReactNode, useMemo, useState } from 'react';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Box, useTheme } from '@mui/material';

import { SelectDropdown, Tooltip } from '@zendesk/forethought-elements';
import { useVersionHistory } from './useVersionHistory';
import { formatVersionTimestamp, getVersionDisplayName } from './utils';
import VariantPill from 'src/components/dashboard-pages/solve-insights/common/VariantPill';
import {
  useVariantLookup,
  variantLookupKey,
} from 'src/pages/workflow-builder-edit/utils/buildVariantLookup';
import { useGetAllAutoflowVersionHistoriesV2Query } from 'src/services/workflowBuilderAutoflowApi.ts/workflowBuilderAutoflowApi';
import { useGetRestorationRequirementsBatchQuery } from 'src/services/workflowBuilderAutoflowApi.ts/workflowBuilderAutoflowApi';
import { WorkflowVersionType } from 'src/slices/workflow-preview/types';
import { AutoflowVersionHistorySummary } from 'src/types/workflowBuilderAPITypes';

const RESTORATION_TOOLTIP =
  'This Version cannot be previewed because it references deleted documents. If you would like to preview it create a saved version from this version.';

interface VersionDropdownOption {
  disabled?: boolean;
  label: string;
  optionEndAdornment?: ReactNode;
  value: string;
}

type VersionDisplayState = 'live' | 'offline' | 'auto-saved';

const STATE_LABELS: Record<VersionDisplayState, string> = {
  'auto-saved': 'Auto-Saved',
  live: 'Live',
  offline: 'Offline',
};

interface VersionSelectDropdownProps {
  /** Disable the dropdown */
  disabled?: boolean;
  /** Error message to display */
  error?: string;
  /** Unique ID for the dropdown */
  id: string;
  /** Intent workflow ID to fetch versions for */
  intentWorkflowId: string;
  /** Whether the dropdown is in "state mode" (shows Live/Offline/Auto-saved as selected) */
  isStateMode?: boolean;
  /** Whether to virtualize the menu for large lists */
  isVirtualized?: boolean;
  /** Whether the workflow is active (affects Live/Offline label) */
  isWorkflowActive?: boolean;
  /** Callback when version is selected */
  onChange: (version: number, versionType: WorkflowVersionType) => void;
  /** Callback when dropdown is closed */
  onClose?: () => void;
  /** Callback when dropdown is opened */
  onOpen?: () => void;
  /** Callback when a state option (Live/Offline/Auto-saved) is selected */
  onStateSelect?: () => void;
  /** Placeholder text */
  placeholder?: string;
  /** Hide the live version from the dropdown (used by non-control A/B test variants) */
  hideLiveVersion?: boolean;
  /** Only show published versions in the dropdown */
  publishedOnly?: boolean;
  /** Make the input read-only */
  readOnly?: boolean;
  /** Currently selected version number */
  selectedVersion?: number | null;
  /** Currently selected version type */
  selectedVersionType?: WorkflowVersionType | null;
  /** Size of the dropdown */
  size?: 'small' | 'medium';
  /** Skip fetching version history */
  skip?: boolean;
  /** Use bulk API endpoint (for rendering many dropdowns efficiently) */
  useBulkApi?: boolean;
}

/**
 * Reusable version select dropdown that shows:
 * - Current (live/offline + A/B test versions)
 * - Versions (saved versions)
 * - Publish history (published versions)
 */
export const VersionSelectDropdown = ({
  disabled,
  error,
  hideLiveVersion = false,
  id,
  intentWorkflowId,
  isStateMode = false,
  isVirtualized = false,
  isWorkflowActive = true,
  onChange,
  onClose,
  onOpen,
  onStateSelect,
  placeholder = 'Select version',
  publishedOnly = false,
  readOnly,
  selectedVersion,
  selectedVersionType,
  size,
  skip = false,
  useBulkApi = false,
}: VersionSelectDropdownProps) => {
  const { palette } = useTheme();

  // Use bulk API when rendering many dropdowns (e.g., in intent filter modal)
  const { data: allVersionHistories } =
    useGetAllAutoflowVersionHistoriesV2Query(undefined, {
      skip: !useBulkApi || skip || !intentWorkflowId,
    });

  const prefetchedData = useBulkApi
    ? allVersionHistories?.[intentWorkflowId]
    : undefined;

  const { currentVersions, isLoading, namedVersions, publishedVersions } =
    useVersionHistory({
      intentWorkflowId,
      prefetchedData,
      skip: skip || !intentWorkflowId,
    });

  const variantLookup = useVariantLookup(intentWorkflowId);

  // Lazy load restoration requirements when dropdown is opened
  const [hasOpenedDropdown, setHasOpenedDropdown] = useState(false);

  const versionsToCheck = useMemo(
    () =>
      publishedVersions
        .filter(v => !v.is_published)
        .map(v => ({ version: v.version, versionType: v.version_type })),
    [publishedVersions],
  );

  const { data: restorationMap = {}, isLoading: isLoadingRestorations } =
    useGetRestorationRequirementsBatchQuery(
      { intentWorkflowId, versions: versionsToCheck },
      {
        skip:
          !hasOpenedDropdown || !intentWorkflowId || !versionsToCheck.length,
      },
    );

  const handleOpen = () => {
    setHasOpenedDropdown(true);
    onOpen?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  // Determine display state for primary current version
  const primaryVersion = currentVersions[0];
  const versionDisplayState = getDisplayState(primaryVersion, isWorkflowActive);

  const options = useMemo(() => {
    const result: VersionDropdownOption[] = [];

    // Current section
    const filteredCurrentVersions = publishedOnly
      ? currentVersions.filter(v => v.is_published)
      : hideLiveVersion
        ? currentVersions.filter(v => !v.is_published)
        : currentVersions;

    if (filteredCurrentVersions.length > 0) {
      result.push({
        disabled: true,
        label: 'Current',
        value: 'current-header',
      });

      filteredCurrentVersions.forEach((version, index) => {
        const variantInfo =
          version.ab_test_variants?.[0] ||
          variantLookup.get(
            variantLookupKey(version.version, version.version_type),
          );

        const label = getVersionLabel(version, index, versionDisplayState);

        result.push({
          label,
          optionEndAdornment: variantInfo ? (
            <Box sx={{ ml: '8px' }}>
              <VariantPill shouldShowOnlyOnActive variant={variantInfo} />
            </Box>
          ) : undefined,
          value:
            index === 0
              ? `state-${versionDisplayState}`
              : `current-${version.version_type}-${version.version}`,
        });
      });
    }

    // Versions section (saved versions)
    if (namedVersions.length > 0 && !publishedOnly) {
      result.push({
        disabled: true,
        label: `Versions (${namedVersions.length})`,
        value: 'versions-header',
      });

      namedVersions.forEach(version => {
        const variantInfo =
          version.ab_test_variants?.[0] ||
          variantLookup.get(
            variantLookupKey(version.version, version.version_type),
          );
        const prefix =
          version.version_type === 'auto_saved' ? 'auto_saved' : 'saved';

        result.push({
          label: getVersionDisplayName(version) || version.version_name,
          optionEndAdornment: variantInfo ? (
            <Box sx={{ ml: '8px' }}>
              <VariantPill shouldShowOnlyOnActive variant={variantInfo} />
            </Box>
          ) : undefined,
          value: `${prefix}-${version.version}`,
        });
      });
    }

    // Publish history section (hidden when publishedOnly since live version is already in Current)
    const filteredPublishedVersions = hideLiveVersion
      ? publishedVersions.filter(v => !v.is_published)
      : publishedVersions;

    if (filteredPublishedVersions.length > 0 && !publishedOnly) {
      result.push({
        disabled: true,
        label: `Publish history (${filteredPublishedVersions.length})`,
        value: 'published-header',
      });

      filteredPublishedVersions.forEach(version => {
        const variantInfo =
          version.ab_test_variants?.[0] ||
          variantLookup.get(
            variantLookupKey(version.version, version.version_type),
          );
        const date = formatVersionTimestamp(version.last_updated_at);
        const key = `published-${version.version}`;
        const needsRestoration =
          !version.is_published && restorationMap[key] === true;

        result.push({
          disabled: isLoadingRestorations || needsRestoration,
          label: `${date}${version.is_published ? ' (Current)' : ''}`,
          optionEndAdornment:
            variantInfo || needsRestoration ? (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'inline-flex',
                  gap: '8px',
                  marginLeft: '8px',
                }}
              >
                {variantInfo && (
                  <VariantPill shouldShowOnlyOnActive variant={variantInfo} />
                )}
                {needsRestoration && (
                  <Tooltip tooltipContent={RESTORATION_TOOLTIP}>
                    <Box
                      component='span'
                      sx={{ display: 'inline-flex', pointerEvents: 'auto' }}
                    >
                      <InfoOutlined
                        sx={{ color: palette.grey[400], fontSize: 16 }}
                      />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            ) : undefined,
          value: key,
        });
      });
    }

    return result;
  }, [
    currentVersions,
    hideLiveVersion,
    isLoadingRestorations,
    namedVersions,
    palette,
    publishedOnly,
    publishedVersions,
    restorationMap,
    variantLookup,
    versionDisplayState,
  ]);

  // Compute current value from selectedVersion/selectedVersionType
  const value = useMemo(() => {
    // If in state mode (e.g. preview filter), show the state value
    if (isStateMode) {
      return `state-${versionDisplayState}`;
    }

    if (selectedVersion === undefined || selectedVersion === null) {
      return '';
    }

    // Check if selected version is in currentVersions (non-primary)
    const isInCurrentVersions = currentVersions.some(
      (v, index) =>
        index > 0 &&
        v.version === selectedVersion &&
        v.version_type === selectedVersionType,
    );

    if (isInCurrentVersions) {
      return `current-${selectedVersionType}-${selectedVersion}`;
    }

    // Check if it's the primary current version
    if (
      primaryVersion &&
      primaryVersion.version === selectedVersion &&
      primaryVersion.version_type === selectedVersionType
    ) {
      return `state-${versionDisplayState}`;
    }

    return `${selectedVersionType}-${selectedVersion}`;
  }, [
    currentVersions,
    isStateMode,
    primaryVersion,
    selectedVersion,
    selectedVersionType,
    versionDisplayState,
  ]);

  const handleChange = (e: { target: { value: string } }) => {
    const selectedValue = e.target.value;

    // Handle state selections (Live/Offline/Auto-saved)
    if (selectedValue.startsWith('state-')) {
      if (onStateSelect) {
        onStateSelect();
        return;
      }
      // Fall through to parse the primary version if no onStateSelect handler
    }

    const parsed = parseValue(selectedValue, currentVersions);
    if (parsed) {
      onChange(parsed.version, parsed.versionType as WorkflowVersionType);
    }
  };

  return (
    <SelectDropdown
      disabled={disabled || isLoading}
      error={error}
      id={id}
      inputProps={{ readOnly }}
      isMenuSearchable
      isVirtualized={isVirtualized}
      onChange={handleChange}
      onClose={handleClose}
      onOpen={handleOpen}
      options={options}
      placeholder={placeholder}
      shouldErrorMessageTakeUpSpace={false}
      size={size}
      value={value}
    />
  );
};

function getDisplayState(
  version: AutoflowVersionHistorySummary | undefined,
  isWorkflowActive: boolean,
): VersionDisplayState {
  if (!version) {
    return 'auto-saved';
  }
  if (version.is_published === true && isWorkflowActive) {
    return 'live';
  }
  if (version.version_type === 'auto_saved') {
    return 'auto-saved';
  }
  return 'offline';
}

function getVersionLabel(
  version: AutoflowVersionHistorySummary,
  index: number,
  displayState: VersionDisplayState,
): string {
  if (version.version_type === 'auto_saved') {
    return 'Auto-Saved';
  }
  if (index === 0) {
    return STATE_LABELS[displayState] ?? displayState;
  }
  return getVersionDisplayName(version) || `Version ${version.version}`;
}

function parseValue(
  value: string,
  currentVersions: AutoflowVersionHistorySummary[],
): { version: number; versionType: string } | null {
  if (value.startsWith('state-')) {
    // For state selections, return the primary current version
    const primaryVersion = currentVersions[0];
    if (primaryVersion) {
      return {
        version: primaryVersion.version,
        versionType: primaryVersion.version_type,
      };
    }
    return null;
  }

  // Strip 'current-' prefix if present
  const normalizedValue = value.startsWith('current-')
    ? value.substring('current-'.length)
    : value;

  const lastDashIndex = normalizedValue.lastIndexOf('-');
  const versionType = normalizedValue.substring(0, lastDashIndex);
  const version = parseInt(normalizedValue.substring(lastDashIndex + 1), 10);

  if (!isNaN(version)) {
    return { version, versionType };
  }

  return null;
}
